import { Injectable } from '@angular/core';
import { Subject, ReplaySubject, Observable, of } from 'rxjs';
import { CustomerRule } from '../../model/customer-rule';
import { Customer } from '../../model/customer';
import { CustomerGroup } from '../../model/customer-group';
import { UserApiService } from '@xpo-ltl/sdk-user';
import { map, take } from 'rxjs/operators';
import { User } from '@xpo-ltl/sdk-common';
import { ConfigManagerService } from '@xpo-ltl/config-manager';
import { ConfigManagerPropertiesEnum } from '../../enums/config-manager-properties.enum';

@Injectable()
export class CustomerRuleService {
  private customerDetailsSubject = new ReplaySubject<Customer>(1);
  public customerDetails$ = this.customerDetailsSubject.asObservable();

  private customerGoupDetailsSubject = new ReplaySubject<CustomerGroup>(1);
  public customerGoupDetails$ = this.customerGoupDetailsSubject.asObservable();

  private customerRuleSubject: Subject<CustomerRule> = new ReplaySubject<CustomerRule>(1);
  public customerRule$ = this.customerRuleSubject.asObservable();
  public user: User;

  constructor(private userApiService: UserApiService, private configManagerService: ConfigManagerService) {}

  /**
   * Check Fixed Roles against User roles
   * @param {string[]} userRoles
   * @param {string[]} matchingRoles
   * @returns {boolean}
   */
  private static findRoleMatch(userRoles: string[], matchingRoles: string[]): boolean {
    let hasRole = false;

    if (!matchingRoles || matchingRoles.length === 0) {
      return true;
    }

    if (!userRoles || userRoles.length === 0) {
      return false;
    }

    for (let a = 0; a < userRoles.length && !hasRole; a++) {
      for (const matchingRole of matchingRoles) {
        if (userRoles[a].endsWith('/' + matchingRole)) {
          hasRole = true;
          break;
        }
      }
    }

    return hasRole;
  }

  /**
   * Returns true when the user can edit or create FIA rules.
   * AD Group: LTL_INVOICE_ACCURACY_RULE_MAINT
   * PFM Variable: InvoiceAccuracyRuleEditRoles
   * @returns {boolean}
   */
  canEditFiaRules(): Observable<boolean> {
    const roles = this.configManagerService.getSetting<Array<string>>(
      ConfigManagerPropertiesEnum.InvoiceAccuracyRuleEditRoles
    );
    if (!this.user || !roles || roles.length === 0) {
      return of(false);
    }
    return this.hasRoles(roles);
  }

  public setCustomerDetails(customerDetails: Customer) {
    this.customerDetailsSubject.next(customerDetails);
  }

  public setCustomerRule(customerRule: CustomerRule) {
    this.customerRuleSubject.next(customerRule);
  }

  public setCustomerGroupDetails(customerGroupDetails: CustomerGroup) {
    this.customerGoupDetailsSubject.next(customerGroupDetails);
  }

  /**
   * Check for associated role(s)
   * @param {string[]} roles
   * @returns Promise<boolean>
   */
  public hasRoles(roles: string[]): Observable<boolean> {
    return this.userApiService.getLoggedInUser().pipe(
      take(1),
      map((user) => {
        return CustomerRuleService.findRoleMatch(user.roles, roles);
      })
    );
  }
}
